import { useEffect, useState } from 'react';

import { Col, ErrorSummary, FormRadioGroup, Row } from '@snsw/react-component-library';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { isEmpty, validateExt } from 'shared/utils/Validation';
import { PagePath } from 'shared/constants/pagePath';

import { saveApplication } from 'shared/redux/applicationSlice';
import ScrollToTop from 'shared/utils/scrollToTop';
import { IndividualHolderFields } from './individual/IndividualHolderFields';
import { storePlantDesignHolderDetails } from 'plantDesign/redux/plantDesignRegistrationHolderSlice';
import { OrganisationHolderFields } from './organisation/OrganisationHolderFields';
import { PlantDesignRegistrationHolderPage } from 'plantDesign/constants/plantDesignRegistrationHolderPage';
import { PasswordlessContinueSaveExitButtons } from 'shared/components/formNavigationButtons/PasswordlessContinueSaveExitButtons';
import _ from 'lodash';
import { PlantDesignConstants } from 'plantDesign/constants/plantDesignConstants';

export const PlantDesignRegistrationHolder = (props) => {
  ScrollToTop();

  const dispatch = useDispatch();
  const savedHolderDetails = useSelector((state) => state.plantDesignHolderDetails);
  const navigate = useNavigate();

  const registrationTypeOptions = [
    {
      value: 'organisation',
      label: 'Organisation',
      clarify: 'You have an ABN, ACN or an ARBN',
    },
    {
      value: 'individual',
      label: 'Individual',
      clarify: 'You are not a business, sole trader or partnership',
    },
  ];

  const holderComponentRef = React.createRef();

  const [registrationTypeValue, setRegistrationTypeValue] = useState('');
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [holderDetails, setHolderDetails] = useState({});

  const [errorsSummary, setErrorsSummary] = useState([]);
  const [holderErrorSummary, setHolderErrorSummary] = useState([]);
  const [combinedErrorSummary, setCombinedErrorSummary] = useState([]);

  useEffect(() => {
    setRegistrationTypeValue(savedHolderDetails?.registrationType ?? '');
    setHolderDetails(_.cloneDeep(savedHolderDetails));
  }, [savedHolderDetails]);

  useEffect(() => {
    if (showErrors) validateExt(validators, setErrors, setErrorsSummary);
  }, [registrationTypeValue]);

  useEffect(() => {
    setCombinedErrorSummary([].concat(errorsSummary, holderErrorSummary));
  }, [errorsSummary, holderErrorSummary]);

  const onChangeEvent = (v) => {
    setRegistrationTypeValue(v);
  };

  const displayRegistrationFields = (registrationType) => {
    if (registrationType === 'organisation') {
      return (
        <OrganisationHolderFields
          ref={holderComponentRef}
          onChange={setHolderDetails}
          value={holderDetails}
          onErrorChange={setHolderErrorSummary}
        />
      );
    } else if (registrationType === 'individual') {
      return (
        <IndividualHolderFields
          ref={holderComponentRef}
          onChange={setHolderDetails}
          value={holderDetails}
          onErrorChange={setHolderErrorSummary}
        />
      );
    }
  };

  const validators = [
    {
      fieldName: 'registrationTypeValue',
      fieldValue: registrationTypeValue,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  const validateAll = () => {
    let isCurrentComponentValid = validateExt(validators, setErrors, setErrorsSummary);

    let isHolderValid = false;
    if (holderComponentRef.current) isHolderValid = holderComponentRef.current.validate();

    return isCurrentComponentValid && isHolderValid;
  };

  const storeDetails = (isCompleted) => {
    let details = {
      isCompleted,
      registrationType: registrationTypeValue,
      ...holderDetails,
    };
    dispatch(storePlantDesignHolderDetails(details));
  };

  const onContinueClick = () => {
    let completed = validateAll();
    if (completed) {
      storeDetails(completed);

      dispatch(saveApplication(true))
        .unwrap()
        .then(() => {
          setShowErrors(false);
          navigate(PagePath.plantDesignDetails);
        })
        .catch(() => {});
    } else {
      setShowErrors(true);
      window.scrollTo(0, 0);
    }
  };

  const onPreSaving = () => {
    let completed = validateAll();
    storeDetails(completed);
  };

  const onBackClick = () => {
    let completed = validateAll();
    storeDetails(completed);
    navigate(PagePath.plantDesignApplicantDetails);
  };

  return (
    <>
      <Row>
        <Col span={8}>
          <ErrorSummary errors={combinedErrorSummary} />
          <FormRadioGroup
            id="registrationTypeValue"
            name="registrationTypeValue"
            vertical={true}
            options={registrationTypeOptions}
            onChange={onChangeEvent}
            value={registrationTypeValue}
            legend={PlantDesignRegistrationHolderPage.registrationTypeLabel}
            errorMessage={errors?.registrationTypeValue?.errorMessage}
            hasError={showErrors && errors?.registrationTypeValue?.invalid}
            margin={{ top: 'none' }}
          />

          {displayRegistrationFields(registrationTypeValue)}
        </Col>
      </Row>
      <PasswordlessContinueSaveExitButtons
        onContinueClick={onContinueClick}
        onPreSaving={onPreSaving}
        onBackClick={onBackClick}
        licenceType={PlantDesignConstants.licenceType}
      />
    </>
  );
};
